/** Site Operations > Service Requests (Demo Mode only) - simplified, site-facing view of the
 * site's own service requests and their progress; a site user can view status but cannot
 * triage, reassign, or manage SLA (that's the central Service Requests screen under Estate
 * Management). No backend exists yet - all data is static mock data, matching the same
 * "seeded illustrative numbers" approach used by src/pages/EstateManagement. */

import { Box, Chip, Grid, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import { Assignment, CheckCircle } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';

const MOCK_REQUESTS = [
  { id: 'SR-1044', issue: 'Servery counter light out', priority: 'Low', status: 'In Progress', raised: '2026-06-03', updated: '2026-06-04' },
  { id: 'SR-1039', issue: 'Combi oven service', priority: 'Medium', status: 'Resolved', raised: '2026-05-20', updated: '2026-05-27' },
  { id: 'SR-1021', issue: 'Chiller door seal replacement', priority: 'High', status: 'Closed', raised: '2026-04-02', updated: '2026-04-09' },
];

const statusColour = (status: string) => {
  if (status === 'Resolved' || status === 'Closed') return 'success';
  if (status === 'In Progress') return 'info';
  return 'default';
};

const priorityColour = (priority: string) => {
  if (priority === 'Critical') return 'error';
  if (priority === 'High') return 'warning';
  return 'default';
};

const SiteOperationsServiceRequests = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [page, setPage] = useState<number>(0);

  const open = MOCK_REQUESTS.filter((r) => !['Resolved', 'Closed'].includes(r.status)).length;

  return (
    <PageArea>
      <PageTitle>
        {t('siteOperationsServiceRequestsTitle', 'Service Requests')}
      </PageTitle>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        <Grid item
          xs={12}
          md={6}>
          <Section style={{ p: 4, mb: 0 }}>
            <Box display="flex"
              alignItems="center"
              justifyContent="space-between">
              <Box flex={1}>
                <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                  {t('serviceRequestsOpen', 'Open requests')}
                </Box>
                <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                  {open}
                </Box>
                <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                  {t('serviceRequestsOpenSubtitle', 'Awaiting resolution')}
                </Box>
              </Box>
              <Box sx={{ backgroundColor: '#ed6c0220', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ed6c02' }}>
                <SvgIcon fontSize="medium">
                  <Assignment />
                </SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
        <Grid item
          xs={12}
          md={6}>
          <Section style={{ p: 4, mb: 0 }}>
            <Box display="flex"
              alignItems="center"
              justifyContent="space-between">
              <Box flex={1}>
                <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                  {t('serviceRequestsTotal', 'Total requests (all time)')}
                </Box>
                <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                  {MOCK_REQUESTS.length}
                </Box>
                <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                  {t('serviceRequestsTotalSubtitle', 'Since site went live')}
                </Box>
              </Box>
              <Box sx={{ backgroundColor: '#1976d220', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1976d2' }}>
                <SvgIcon fontSize="medium">
                  <CheckCircle />
                </SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
      </Grid>

      <Section>
        <TableHeader>
          {t('siteOperationsServiceRequestsListTitle', 'My site requests')}
          <Pill text={`${MOCK_REQUESTS.length} ${t('serviceRequestsId', 'Requests')}`} />
        </TableHeader>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('serviceRequestsId', 'Request ID')}</TableCell>
                <TableCell>{t('siteEquipmentHistoryType', 'Issue')}</TableCell>
                <TableCell>{t('serviceRequestsPriority', 'Priority')}</TableCell>
                <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
                <TableCell>{t('serviceRequestsRaised', 'Raised')}</TableCell>
                <TableCell>{t('siteOperationsLastUpdated', 'Last Updated')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {MOCK_REQUESTS.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.issue}</TableCell>
                  <TableCell>
                    <Chip label={row.priority}
                      size="small"
                      color={priorityColour(row.priority)}
                      variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={row.status}
                      size="small"
                      color={statusColour(row.status)}
                      variant="outlined" />
                  </TableCell>
                  <TableCell>{row.raised}</TableCell>
                  <TableCell>{row.updated}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomPagination useNoDataMessage={false}
          page={page}
          setPage={setPage}
          maxPages={1}
          breakpointForChangeDisplay={120000} />
      </Section>
    </PageArea>
  );
};

export default SiteOperationsServiceRequests;
